<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  CRASH_ISSUE_STATUSES,
  type CrashIssueApi,
  type CrashIssueStatus,
  type CrashStatsTrendPeriod,
  type CrashStatsTrendView
} from '@xjlv2/api-client'
import { useCrashIssues } from '../composables/useCrashIssues'

const props = defineProps<{
  createApiClient: () => CrashIssueApi
  canView: boolean
}>()

const {
  view,
  page,
  size,
  status,
  list,
  detail,
  trendPeriod,
  trend,
  listLoading,
  listError,
  detailLoading,
  detailError,
  trendLoading,
  trendError,
  statusUpdating,
  statusError,
  loadList,
  loadTrend,
  reset,
  openDetail,
  backToList,
  updateStatus
} = useCrashIssues({
  createApiClient: props.createApiClient,
  canView: () => props.canView
})

const keywordDraft = ref('')
const transitionStatus = ref<CrashIssueStatus>('NEW')
const assigneeDraft = ref('')
/** 堆栈视图切换：还原优先（有还原帧时），无还原退化为仅原始。 */
const stackMode = ref<'retraced' | 'original'>('retraced')

watch(
  () => props.canView,
  (canView) => {
    // 权限态变化即整体重置：输入框草稿随 composable keyword 一并清空（T7 评审
    // 必修④），避免再次授权后草稿与实际过滤条件错位。列表与趋势并行加载。
    keywordDraft.value = ''
    if (canView) {
      void loadList()
      void loadTrend()
    } else {
      reset()
    }
  },
  { immediate: true }
)

const STATUS_LABELS: Record<CrashIssueStatus, string> = {
  NEW: '新问题',
  PROCESSING: '处理中',
  RESOLVED: '已解决',
  IGNORED: '已忽略'
}

const statusOptions: Array<{ value: CrashIssueStatus | null; label: string }> = [
  { value: null, label: '全部状态' },
  ...CRASH_ISSUE_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))
]

const pageSizeOptions = [20, 50, 100]

/** 趋势条周期（P2 Task 5 口径：7/30 天；YESTERDAY 镜像保留在 api-client）。 */
const trendPeriodOptions: Array<{ value: CrashStatsTrendPeriod; label: string }> = [
  { value: 'LAST_7_DAYS', label: '近 7 天' },
  { value: 'LAST_30_DAYS', label: '近 30 天' }
]

const totalPages = computed(() => {
  const total = list.value?.total ?? 0
  return Math.max(1, Math.ceil(total / size.value))
})

const isBusy = computed(() => listLoading.value || detailLoading.value || statusUpdating.value)

const countFormatter = new Intl.NumberFormat('zh-CN')

function formatCount(value: number): string {
  return Number.isFinite(value) ? countFormatter.format(value) : '-'
}

/** T3 评审必办：次数 = eventCount + droppedCount（风暴日限流丢弃计入，避免饱和失真）。 */
function totalOccurrences(eventCount: number, droppedCount: number): number {
  return eventCount + droppedCount
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function textOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : '—'
}

function statusLabel(value: CrashIssueStatus): string {
  return STATUS_LABELS[value] ?? value
}

function submitKeyword(): void {
  void loadList({ page: 1, keyword: keywordDraft.value })
}

function changeStatusFilter(): void {
  void loadList({ page: 1 })
}

function changePageSize(): void {
  void loadList({ page: 1 })
}

function changeTrendPeriod(): void {
  void loadTrend(trendPeriod.value)
}

function goPage(nextPage: number): void {
  if (nextPage < 1 || nextPage > totalPages.value) return
  void loadList({ page: nextPage })
}

function submitTransition(): void {
  const assigneeText = assigneeDraft.value.trim()
  const assigneeId = assigneeText ? Number(assigneeText) : null
  if (assigneeId !== null && (!Number.isInteger(assigneeId) || assigneeId <= 0)) {
    statusError.value = '处理人 ID 须为正整数'
    return
  }
  void updateStatus(transitionStatus.value, assigneeId)
}

// ---------------------------------------------------------------- 趋势条（traffic 手法）

/** 单日条形展示值：宽度 = 值 / 窗口峰值（0/无数据 → 0% 宽，值照常显示）。 */
function trendBarValue(value: number, maximum: number): { label: string; width: string } {
  const percentage =
    value > 0 && maximum > 0 ? Math.max(0, Math.min(100, (value / maximum) * 100)) : 0
  return { label: formatCount(value), width: `${Number(percentage.toFixed(2))}%` }
}

const trendBars = computed(() => {
  const series = trend.value?.series ?? []
  const maximum = series.reduce(
    (peak, point) => Math.max(peak, point.events, point.users),
    0
  )
  return series.map((point) => ({
    day: point.localDate.slice(5),
    events: trendBarValue(point.events, maximum),
    users: trendBarValue(point.users, maximum)
  }))
})

const trendSummary = computed(() => {
  const current: CrashStatsTrendView | null = trend.value
  if (!current) return ''
  return `${current.fromDate} ~ ${current.toDate} · 事件 ${formatCount(current.totalEvents)} · 用户 ${formatCount(current.totalUsers)}`
})

// ---------------------------------------------------------------- 详情堆栈区

const latestEvent = computed(() => detail.value?.recentEvents[0] ?? null)

const latestEventHasRetraced = computed(
  () => (latestEvent.value?.retracedStack?.length ?? 0) > 0
)

watch(detail, (next) => {
  if (!next) return
  // 流转控件跟随当前详情状态；指派输入每次进入详情重置（缺省=保持现值）。
  transitionStatus.value = next.issue.status
  assigneeDraft.value = ''
  // 堆栈视图默认还原优先；最新事件无还原帧时退化为原始。
  stackMode.value = latestEventHasRetraced.value ? 'retraced' : 'original'
})

/** 堆栈区展示帧：还原模式取还原值（resolved 帧优先 resolved*，未命中回退原始）。 */
const displayedFrames = computed(() => {
  const event = latestEvent.value
  if (!event) return []
  if (stackMode.value === 'retraced' && event.retracedStack) {
    return event.retracedStack.map((frame) => ({
      symbol: frame.resolved && frame.resolvedSymbol ? frame.resolvedSymbol : frame.symbol,
      file: frame.resolved && frame.resolvedFile ? frame.resolvedFile : frame.file,
      line: frame.resolved && frame.resolvedLine !== null ? frame.resolvedLine : frame.line,
      resolved: frame.resolved
    }))
  }
  return event.stack.map((frame) => ({
    symbol: frame.symbol,
    file: frame.file,
    line: frame.line,
    resolved: false
  }))
})

function frameLocation(file: string | null, line: number | null): string {
  if (!file && line === null) return '—'
  if (!file) return `:${line ?? ''}`
  return line === null ? file : `${file}:${line}`
}
</script>

<template>
  <section class="crash-issues" :aria-busy="isBusy ? 'true' : 'false'">
    <header class="crash-issues__header">
      <div class="crash-issues__heading">
        <p>租户稳定性</p>
        <h2>错误监控</h2>
      </div>

      <form v-if="view === 'list'" class="crash-issues__filters" @submit.prevent="submitKeyword">
        <select
          v-model="status"
          class="ren-control crash-issues__status-filter"
          aria-label="错误监控状态过滤"
          @change="changeStatusFilter"
        >
          <option
            v-for="option in statusOptions"
            :key="option.value ?? 'ALL'"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <input
          v-model="keywordDraft"
          class="ren-control ren-input crash-issues__keyword"
          type="search"
          aria-label="错误监控关键词搜索"
          placeholder="搜索问题标题"
        />
        <button class="ren-button crash-issues__search" type="submit">搜索</button>
      </form>
    </header>

    <!-- 列表视图 -->
    <template v-if="view === 'list'">
      <!-- 顶部趋势条（P2 Task 5）：events/users 按日纯 CSS 条形（traffic 手法），
           与列表加载并行（独立 loading/error 态，互不打断）。 -->
      <section
        v-if="canView"
        class="crash-issues__trend"
        aria-labelledby="crash-trend-title"
      >
        <div class="crash-issues__trend-heading">
          <h3 id="crash-trend-title">崩溃趋势</h3>
          <ul class="crash-issues__trend-legend" aria-label="趋势图图例">
            <li>
              <span class="crash-issues__trend-dot crash-issues__trend-dot--events" aria-hidden="true"></span>
              事件
            </li>
            <li>
              <span class="crash-issues__trend-dot crash-issues__trend-dot--users" aria-hidden="true"></span>
              用户
            </li>
          </ul>
          <select
            v-model="trendPeriod"
            class="ren-control crash-issues__trend-period"
            aria-label="崩溃趋势周期"
            @change="changeTrendPeriod"
          >
            <option v-for="option in trendPeriodOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div
          v-if="trendLoading"
          class="crash-issues__trend-state"
          aria-live="polite"
          role="status"
        >
          趋势数据加载中
        </div>
        <div
          v-else-if="trendError"
          class="crash-issues__trend-state crash-issues__trend-state--error"
          aria-live="polite"
          role="status"
        >
          {{ trendError }}
        </div>
        <template v-else-if="trend">
          <p class="crash-issues__trend-summary">{{ trendSummary }}</p>
          <div class="crash-issues__trend-scroll">
            <ol class="crash-issues__trend-bars" aria-label="崩溃趋势（事件与用户按日）">
              <li
                v-for="point in trendBars"
                :key="point.day"
                class="crash-issues__trend-point"
              >
                <span class="crash-issues__trend-day">{{ point.day }}</span>
                <span class="crash-issues__trend-rows">
                  <span class="crash-issues__trend-row" :aria-label="`事件 ${point.events.label}`">
                    <span class="crash-issues__trend-track" aria-hidden="true">
                      <span
                        class="crash-issues__trend-bar crash-issues__trend-bar--events"
                        :style="{ width: point.events.width }"
                      ></span>
                    </span>
                    <span class="crash-issues__trend-value">{{ point.events.label }}</span>
                  </span>
                  <span class="crash-issues__trend-row" :aria-label="`用户 ${point.users.label}`">
                    <span class="crash-issues__trend-track" aria-hidden="true">
                      <span
                        class="crash-issues__trend-bar crash-issues__trend-bar--users"
                        :style="{ width: point.users.width }"
                      ></span>
                    </span>
                    <span class="crash-issues__trend-value">{{ point.users.label }}</span>
                  </span>
                </span>
              </li>
            </ol>
          </div>
        </template>
        <div v-else class="crash-issues__trend-state" aria-live="polite" role="status">
          暂无趋势数据
        </div>
      </section>
      <div
        v-if="!canView"
        class="crash-issues__state crash-issues__state--disabled"
        aria-live="polite"
        role="status"
      >
        <strong>无权访问错误监控</strong>
        <span>当前账号没有查看错误监控所需的系统权限</span>
      </div>
      <div
        v-else-if="listLoading"
        class="crash-issues__state crash-issues__state--loading"
        aria-live="polite"
        role="status"
      >
        <strong>问题列表加载中</strong>
        <span>正在获取错误监控 issue 列表</span>
      </div>
      <div
        v-else-if="listError || !list"
        class="crash-issues__state crash-issues__state--unavailable"
        aria-live="polite"
        role="status"
      >
        <strong>问题列表暂不可用</strong>
        <span>{{ listError || '暂时无法读取错误监控数据' }}</span>
      </div>
      <div
        v-else-if="list.items.length === 0"
        class="crash-issues__state crash-issues__state--empty"
        aria-live="polite"
        role="status"
      >
        <strong>尚无错误记录</strong>
        <span>当前筛选条件下没有可展示的 issue</span>
      </div>
      <template v-else>
        <div class="crash-issues__table-scroll">
          <table>
            <caption class="crash-issues__sr-only">
              错误监控 issue 列表（标题 / 平台 / 版本 / 次数 / 影响用户 / 最近发生 / 状态）
            </caption>
            <thead>
              <tr>
                <th scope="col">问题标题</th>
                <th scope="col">平台</th>
                <th scope="col">版本</th>
                <th scope="col" title="次数 = 事件数 + 限流丢弃数">次数（含丢弃）</th>
                <th scope="col">影响用户</th>
                <th scope="col">最近发生</th>
                <th scope="col">状态</th>
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="issue in list.items" :key="issue.fingerprint">
                <th scope="row">
                  <span class="crash-issues__issue-title">{{ textOrDash(issue.title) }}</span>
                  <span class="crash-issues__issue-type">{{ textOrDash(issue.exceptionType) }}</span>
                </th>
                <td>{{ textOrDash(issue.platform) }}</td>
                <td>{{ textOrDash(issue.appVersion) }}</td>
                <td>{{ formatCount(totalOccurrences(issue.eventCount, issue.droppedCount)) }}</td>
                <td>{{ formatCount(issue.userCount) }}</td>
                <td>{{ formatDateTime(issue.lastSeenAt) }}</td>
                <td>
                  <span
                    class="crash-issues__status"
                    :class="`crash-issues__status--${issue.status.toLowerCase()}`"
                  >
                    {{ statusLabel(issue.status) }}
                  </span>
                </td>
                <td>
                  <button
                    class="ren-button crash-issues__open"
                    type="button"
                    :aria-label="`查看问题详情 ${issue.title}`"
                    @click="openDetail(issue.fingerprint)"
                  >
                    查看
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="crash-issues__note">
          次数 = 事件数 + 限流丢弃数：风暴日限流丢弃计入合计，避免仅看入库事件数的饱和失真。
        </p>
        <nav class="crash-issues__pagination" aria-label="错误监控列表分页">
          <button
            class="ren-button"
            type="button"
            :disabled="page <= 1 || listLoading"
            @click="goPage(page - 1)"
          >
            上一页
          </button>
          <span class="crash-issues__pagination-info">
            第 {{ page }} / {{ totalPages }} 页 · 共 {{ formatCount(list.total) }} 条
          </span>
          <button
            class="ren-button"
            type="button"
            :disabled="page >= totalPages || listLoading"
            @click="goPage(page + 1)"
          >
            下一页
          </button>
          <select
            v-model.number="size"
            class="ren-control crash-issues__page-size"
            aria-label="每页条数"
            @change="changePageSize"
          >
            <option v-for="option in pageSizeOptions" :key="option" :value="option">
              {{ option }} 条/页
            </option>
          </select>
        </nav>
      </template>
    </template>

    <!-- 详情视图 -->
    <template v-else>
      <button class="ren-button crash-issues__back" type="button" @click="backToList">
        返回列表
      </button>

      <div
        v-if="detailLoading"
        class="crash-issues__state crash-issues__state--loading"
        aria-live="polite"
        role="status"
      >
        <strong>问题详情加载中</strong>
        <span>正在获取 issue 投影与最新事件摘要</span>
      </div>
      <div
        v-else-if="detailError || !detail"
        class="crash-issues__state crash-issues__state--unavailable"
        aria-live="polite"
        role="status"
      >
        <strong>问题详情暂不可用</strong>
        <span>{{ detailError || '暂时无法读取该 issue 详情' }}</span>
      </div>
      <template v-else>
        <section class="crash-issues__issue-card" aria-label="issue 概览">
          <div class="crash-issues__issue-heading">
            <h3>{{ textOrDash(detail.issue.title) }}</h3>
            <span
              class="crash-issues__status"
              :class="`crash-issues__status--${detail.issue.status.toLowerCase()}`"
            >
              {{ statusLabel(detail.issue.status) }}
            </span>
          </div>
          <p class="crash-issues__fingerprint">
            指纹 <code>{{ detail.issue.fingerprint }}</code>
          </p>
          <dl class="crash-issues__facts">
            <div>
              <dt>平台</dt>
              <dd>{{ textOrDash(detail.issue.platform) }}</dd>
            </div>
            <div>
              <dt>版本</dt>
              <dd>{{ textOrDash(detail.issue.appVersion) }}</dd>
            </div>
            <div>
              <dt>异常类型</dt>
              <dd>{{ textOrDash(detail.issue.exceptionType) }}</dd>
            </div>
            <div>
              <dt>首次发生</dt>
              <dd>{{ formatDateTime(detail.issue.firstSeenAt) }}</dd>
            </div>
            <div>
              <dt>最近发生</dt>
              <dd>{{ formatDateTime(detail.issue.lastSeenAt) }}</dd>
            </div>
            <div>
              <dt>次数（含丢弃）</dt>
              <dd>
                {{ formatCount(totalOccurrences(detail.issue.eventCount, detail.issue.droppedCount)) }}
              </dd>
            </div>
            <div>
              <dt>限流丢弃</dt>
              <dd>{{ formatCount(detail.issue.droppedCount) }}</dd>
            </div>
            <div>
              <dt>影响用户</dt>
              <dd>{{ formatCount(detail.issue.userCount) }}</dd>
            </div>
            <div>
              <dt>处理人</dt>
              <dd>{{ detail.issue.assigneeId === null ? '未指派' : `用户 ${detail.issue.assigneeId}` }}</dd>
            </div>
          </dl>

          <form class="crash-issues__transition" @submit.prevent="submitTransition">
            <select
              v-model="transitionStatus"
              class="ren-control crash-issues__transition-status"
              aria-label="issue 状态流转"
            >
              <option v-for="value in CRASH_ISSUE_STATUSES" :key="value" :value="value">
                {{ statusLabel(value) }}
              </option>
            </select>
            <input
              v-model="assigneeDraft"
              class="ren-control ren-input crash-issues__assignee"
              type="number"
              min="1"
              aria-label="指派处理人用户 ID（可选）"
              placeholder="指派处理人 ID（可选）"
            />
            <button class="ren-button crash-issues__apply" type="submit" :disabled="statusUpdating">
              {{ statusUpdating ? '流转中…' : '应用流转' }}
            </button>
          </form>
          <p v-if="statusError" class="crash-issues__status-error" role="alert">{{ statusError }}</p>
        </section>

        <section class="crash-issues__events" aria-label="最新事件摘要">
          <h3>最新事件</h3>
          <p v-if="detail.recentEvents.length === 0" class="crash-issues__note">
            暂无事件摘要：事件可能已被保留期清理。
          </p>
          <div v-else class="crash-issues__table-scroll">
            <table>
              <caption class="crash-issues__sr-only">
                最新事件摘要（时间 / 平台 / 操作系统 / 版本 / 设备标识 / 异常消息 / 堆栈首帧）
              </caption>
              <thead>
                <tr>
                  <th scope="col">时间</th>
                  <th scope="col">平台</th>
                  <th scope="col">操作系统</th>
                  <th scope="col">版本</th>
                  <th scope="col">设备标识</th>
                  <th scope="col">异常消息</th>
                  <th scope="col">堆栈首帧</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(event, index) in detail.recentEvents" :key="`${event.occurredAt}|${event.anonymousId}|${index}`">
                  <th scope="row">{{ formatDateTime(event.occurredAt) }}</th>
                  <td>{{ textOrDash(event.platform) }}</td>
                  <td>{{ textOrDash(event.os) }}</td>
                  <td>{{ textOrDash(event.appVersion) }}</td>
                  <td>{{ textOrDash(event.anonymousId) }}</td>
                  <td>{{ textOrDash(event.exceptionMessage) }}</td>
                  <td>{{ event.stackFirstFrame ? event.stackFirstFrame : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 最新事件堆栈区（P2 Task 5）：还原优先（resolvedSymbol+resolvedFile+
             resolvedLine，未命中单帧回退原始），原始/还原 toggle 小控件。 -->
        <section class="crash-issues__stack" aria-labelledby="crash-stack-title">
          <div class="crash-issues__stack-heading">
            <h3 id="crash-stack-title">最新事件堆栈</h3>
            <div class="crash-issues__stack-toggle" role="group" aria-label="堆栈视图切换">
              <button
                type="button"
                class="ren-button crash-issues__stack-mode"
                :class="{ 'is-active': stackMode === 'retraced' }"
                :disabled="!latestEventHasRetraced"
                :aria-pressed="stackMode === 'retraced'"
                @click="stackMode = 'retraced'"
              >
                还原
              </button>
              <button
                type="button"
                class="ren-button crash-issues__stack-mode"
                :class="{ 'is-active': stackMode === 'original' }"
                :aria-pressed="stackMode === 'original'"
                @click="stackMode = 'original'"
              >
                原始
              </button>
            </div>
          </div>
          <p v-if="!latestEvent || latestEvent.stack.length === 0" class="crash-issues__note">
            最新事件无堆栈数据：客户端未采集或保留期已清理。
          </p>
          <template v-else>
            <p v-if="!latestEventHasRetraced" class="crash-issues__note">
              未命中符号文件，仅展示原始堆栈；上传对应 (平台, 版本, 构建) 的符号文件后可还原。
            </p>
            <ol class="crash-issues__stack-frames" aria-label="最新事件堆栈帧">
              <li
                v-for="(frame, index) in displayedFrames"
                :key="index"
                class="crash-issues__stack-frame"
              >
                <span class="crash-issues__frame-index" aria-hidden="true">{{ index + 1 }}</span>
                <span class="crash-issues__frame-symbol">{{ frame.symbol ?? '—' }}</span>
                <span class="crash-issues__frame-location">
                  {{ frameLocation(frame.file, frame.line) }}
                </span>
              </li>
            </ol>
          </template>
        </section>
      </template>
    </template>
  </section>
</template>

<style scoped>
.crash-issues {
  display: grid;
  min-width: 0;
  gap: 20px;
  color: var(--rr-color-text);
}

.crash-issues__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.crash-issues__heading {
  min-width: 0;
}

.crash-issues__heading p {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.crash-issues__heading h2 {
  margin: 3px 0 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-size: 22px;
  line-height: 1.3;
}

.crash-issues__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.crash-issues__status-filter {
  box-sizing: border-box;
  width: 132px;
  padding: 0 30px 0 10px;
}

.crash-issues__keyword {
  box-sizing: border-box;
  width: 200px;
}

.crash-issues__search {
  padding: 0 14px;
}

.crash-issues__state {
  box-sizing: border-box;
  display: grid;
  align-content: center;
  min-height: 88px;
  gap: 4px;
  border-left: 3px solid var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  padding: 16px 18px;
  color: var(--rr-color-text);
}

.crash-issues__state strong {
  color: var(--rr-color-text-strong);
  font-size: 14px;
}

.crash-issues__state span {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.crash-issues__state--loading {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
}

.crash-issues__state--unavailable {
  border-color: var(--rr-color-danger);
  background: var(--rr-color-danger-soft);
}

.crash-issues__state--empty {
  border-color: var(--rr-color-primary);
}

.crash-issues__table-scroll {
  min-width: 0;
  overflow-x: auto;
}

.crash-issues__table-scroll table {
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
  table-layout: fixed;
  color: var(--rr-color-text);
  font-size: 13px;
}

.crash-issues__table-scroll th,
.crash-issues__table-scroll td {
  min-width: 0;
  border-bottom: 1px solid var(--rr-color-border);
  padding: 10px 12px;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
  text-align: right;
  vertical-align: middle;
}

.crash-issues__table-scroll thead th {
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.crash-issues__table-scroll tbody th[scope='row'] {
  color: var(--rr-color-text-strong);
  font-weight: 700;
  text-align: left;
}

.crash-issues__table-scroll td:first-child,
.crash-issues__table-scroll th:first-child {
  text-align: left;
}

.crash-issues__issue-title {
  display: block;
}

.crash-issues__issue-type {
  display: block;
  margin-top: 2px;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.crash-issues__status {
  display: inline-block;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 999px;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.6;
  white-space: nowrap;
}

.crash-issues__status--new {
  border-color: var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
}

.crash-issues__status--processing {
  border-color: var(--rr-color-info-border);
  background: var(--rr-color-info-soft);
  color: var(--rr-color-info);
}

.crash-issues__status--resolved {
  border-color: var(--rr-color-success-border);
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
}

.crash-issues__status--ignored {
  border-color: var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}

.crash-issues__open {
  padding: 0 12px;
  white-space: nowrap;
}

.crash-issues__note {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.crash-issues__pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.crash-issues__pagination-info {
  color: var(--rr-color-text-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.crash-issues__page-size {
  box-sizing: border-box;
  width: 108px;
  padding: 0 30px 0 10px;
}

.crash-issues__back {
  justify-self: start;
  padding: 0 14px;
}

.crash-issues__issue-card {
  display: grid;
  min-width: 0;
  gap: 12px;
  border: 1px solid var(--rr-color-border);
  border-radius: 10px;
  background: var(--rr-color-surface);
  padding: 16px 18px;
}

.crash-issues__issue-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.crash-issues__issue-heading h3 {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-size: 16px;
  line-height: 1.4;
}

.crash-issues__fingerprint {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.crash-issues__fingerprint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: var(--rr-color-text);
}

.crash-issues__facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0;
  border-top: 1px solid var(--rr-color-border);
  border-bottom: 1px solid var(--rr-color-border);
}

.crash-issues__facts > div {
  min-width: 0;
  min-height: 58px;
  padding: 10px 14px;
}

.crash-issues__facts > div + div {
  border-left: 1px solid var(--rr-color-border);
}

.crash-issues__facts dt {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.crash-issues__facts dd {
  margin: 4px 0 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
}

.crash-issues__transition {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.crash-issues__transition-status {
  box-sizing: border-box;
  width: 132px;
  padding: 0 30px 0 10px;
}

.crash-issues__assignee {
  box-sizing: border-box;
  width: 200px;
}

.crash-issues__apply {
  padding: 0 14px;
}

.crash-issues__status-error {
  margin: 0;
  color: var(--rr-color-danger);
  font-size: 13px;
}

.crash-issues__events {
  display: grid;
  min-width: 0;
  gap: 12px;
}

.crash-issues__events h3 {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  line-height: 1.4;
}

.crash-issues__events .crash-issues__table-scroll table {
  min-width: 980px;
}

.crash-issues__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

/* ---------------------------------------------------------------- 趋势条（traffic 手法） */

.crash-issues__trend {
  display: grid;
  min-width: 0;
  gap: 12px;
  border: 1px solid var(--rr-color-border);
  border-radius: 10px;
  background: var(--rr-color-surface);
  padding: 16px 18px;
}

.crash-issues__trend-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.crash-issues__trend-heading h3 {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  line-height: 1.4;
}

.crash-issues__trend-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.crash-issues__trend-legend li {
  display: flex;
  align-items: center;
  gap: 5px;
}

.crash-issues__trend-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
}

.crash-issues__trend-dot--events {
  background: var(--rr-color-danger);
}

.crash-issues__trend-dot--users {
  background: var(--rr-color-info);
}

.crash-issues__trend-period {
  box-sizing: border-box;
  width: 108px;
  padding: 0 30px 0 10px;
  margin-left: auto;
}

.crash-issues__trend-state {
  min-height: 44px;
  border-left: 3px solid var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  padding: 10px 14px;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.crash-issues__trend-state--error {
  border-color: var(--rr-color-danger);
  color: var(--rr-color-danger);
}

.crash-issues__trend-summary {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.crash-issues__trend-scroll {
  min-width: 0;
  overflow-x: auto;
}

.crash-issues__trend-bars {
  display: grid;
  min-width: 560px;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--rr-color-border);
  list-style: none;
}

.crash-issues__trend-point {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  min-height: 44px;
  gap: 12px;
  border-bottom: 1px solid var(--rr-color-border);
}

.crash-issues__trend-day {
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.crash-issues__trend-rows {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.crash-issues__trend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 56px;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.crash-issues__trend-track {
  display: block;
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--rr-color-surface-subtle);
}

.crash-issues__trend-bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  transition: width 0.2s ease;
}

.crash-issues__trend-bar--events {
  background: var(--rr-color-danger-soft);
}

.crash-issues__trend-bar--users {
  background: var(--rr-color-info-soft);
}

.crash-issues__trend-value {
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
}

/* ---------------------------------------------------------------- 详情堆栈区 */

.crash-issues__stack {
  display: grid;
  min-width: 0;
  gap: 12px;
}

.crash-issues__stack-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.crash-issues__stack-heading h3 {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  line-height: 1.4;
}

.crash-issues__stack-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
}

.crash-issues__stack-mode {
  padding: 0 12px;
  font-size: 12px;
}

.crash-issues__stack-mode.is-active {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}

.crash-issues__stack-frames {
  display: grid;
  margin: 0;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: var(--rr-color-surface);
  padding: 8px 0;
  list-style: none;
}

.crash-issues__stack-frame {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) minmax(0, 30%);
  align-items: baseline;
  gap: 10px;
  padding: 5px 14px;
  font-size: 12px;
}

.crash-issues__stack-frame + .crash-issues__stack-frame {
  border-top: 1px solid var(--rr-color-border);
}

.crash-issues__frame-index {
  color: var(--rr-color-text-muted);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
}

.crash-issues__frame-symbol {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.crash-issues__frame-location {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

@media (max-width: 960px) {
  .crash-issues__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .crash-issues__filters {
    width: 100%;
    justify-content: flex-start;
  }

  .crash-issues__facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .crash-issues__facts > div + div {
    border-left: 0;
  }

  .crash-issues__facts > div:nth-child(n + 3) {
    border-top: 1px solid var(--rr-color-border);
  }
}

@media (max-width: 600px) {
  .crash-issues {
    gap: 16px;
  }

  .crash-issues__status-filter,
  .crash-issues__keyword,
  .crash-issues__search,
  .crash-issues__page-size,
  .crash-issues__trend-period,
  .crash-issues__transition-status,
  .crash-issues__assignee,
  .crash-issues__apply {
    width: 100%;
  }

  .crash-issues__trend-period {
    margin-left: 0;
  }

  .crash-issues__stack-frame {
    grid-template-columns: 24px minmax(0, 1fr);
  }

  .crash-issues__frame-location {
    grid-column: 2;
    text-align: left;
  }

  .crash-issues__pagination {
    justify-content: flex-start;
  }

  .crash-issues__pagination-info {
    width: 100%;
    text-align: center;
  }

  .crash-issues__facts {
    grid-template-columns: 1fr;
  }

  .crash-issues__facts > div:nth-child(n + 2) {
    border-top: 1px solid var(--rr-color-border);
  }
}
</style>
